import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { AbstractControl, NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDropdownComponent,
  OnyxIconComponent,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxToggleComponent,
} from '@onyx/angular';
import { map } from 'rxjs';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';

export type FleetLicensesAndPermitsFormGroup = ReturnType<
  typeof FleetLicensesAndPermitsFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-licenses-and-permits-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxToggleComponent,
    OnyxTextFieldComponent,
    NgTemplateOutlet,
    OnyxIconComponent,
  ],
  templateUrl: './fleet-licenses-and-permits-form.component.html',
  styleUrl: './fleet-licenses-and-permits-form.component.scss',
})
export class FleetLicensesAndPermitsFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<FleetLicensesAndPermitsFormGroup>();

  protected readonly I18N = 'fleet.fleetForm.licensesAndPermits';

  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  protected isInvalid!: Signal<boolean>;

  protected hasCommunityLicense = signal(false);
  protected hasPermit = signal(false);

  public ngOnInit(): void {
    const value = this.form().value;
    this.hasCommunityLicense.set(!!value.communityLicenseAbstractNumber);
    this.hasPermit.set(!!value.permitAbstractNumber);

    const controls = this.form().controls;
    const hasAbstractControl = controls.hasAbstract;

    effect(
      () => {
        const hasCommunityLicense = this.hasCommunityLicense();
        const hasPermit = this.hasPermit();
        hasAbstractControl.setValue(hasCommunityLicense || hasPermit);

        setTimeout(() => {
          this.toggleControl(
            controls.communityLicenseAbstractNumber,
            hasCommunityLicense,
          );
          this.toggleControl(controls.permitAbstractNumber, hasPermit);
        });
      },
      { injector: this.injector },
    );

    this.isInvalid = toSignal(
      hasAbstractControl.statusChanges.pipe(
        map(() => hasAbstractControl.invalid && hasAbstractControl.touched),
      ),
      { initialValue: false, injector: this.injector },
    );
  }

  protected toggleControl(control: AbstractControl, enabled: boolean): void {
    ValidationHelper.toggleControls(control, { enabled, reset: true });
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      issuingCountry: fb.control(preferencesService.defaultCountry(), [
        Validators.required,
      ]),
      hasAbstract: fb.control(false, [Validators.requiredTrue]),
      communityLicenseAbstractNumber: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      permitAbstractNumber: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
    });
  }
}
